import { useTranslation } from 'react-i18next'
import { Cell, DataBlock, Grid } from '@fs/zion-ui'
import React, { useContext, useEffect } from 'react'
import DmcFactBlock from '../../common/DmcFactBlock'
import { isNullOrUndefined, isNullOrUndefinedOrEmpty } from '../../../util/Utils'
import { PersonLink, TreePersonLink, UserLink } from '../../app/Links'
import { TreeIdCells } from '../messagesList/messageSummaries/TreeIdBlock'
import { QueuesContext } from '../QueuesContext'

export default () => {}

export function useMessageSummaryData({ messageDetails, queueCachesState, queueCachesActions }) {
  const personId = messageDetails?.personId
  const contributorId = messageDetails?.contributorId

  const personSummary = queueCachesState.personsCache[personId]
  const personLabels = queueCachesState.labelsCache[personId]
  const userSummary = queueCachesState.usersCache[contributorId]

  useEffect(() => {
    queueCachesActions.fetchPersonSummary(personId)
    queueCachesActions.fetchPersonLabels(personId)
    queueCachesActions.fetchUserSummary(contributorId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { personId, contributorId, personSummary, personLabels, userSummary }
}

export function PersonLinks({ personId, personName }) {
  return (
    <>
      <PersonLink personId={personId} personName={personName} showId newTab />
      <TreePersonLink personId={personId} treeIcon newTab />
    </>
  )
}

export function PersonCell({ personId, personSummary, heading }) {
  const { t } = useTranslation()
  return (
    <Cell columns={6}>
      <DataBlock
        heading={heading || t('person.label', 'Person')}
        data={<PersonLinks personId={personId} personName={personSummary?.summary?.name} />}
      />
    </Cell>
  )
}

export function ContributorCell({ contributorId, userSummary }) {
  const { t } = useTranslation()
  return (
    <Cell columns={6}>
      <DataBlock
        heading={t('contributor', 'Contributor')}
        data={<UserLink userId={contributorId} userName={userSummary?.contactName} showId newTab />}
      />
    </Cell>
  )
}

export function PersonAndContributorCells({ personId, personSummary, contributorId, userSummary }) {
  return (
    <>
      <PersonCell personId={personId} personSummary={personSummary} />
      <ContributorCell contributorId={contributorId} userSummary={userSummary} />
    </>
  )
}

export function OriginalAndStandardFactBlock({ heading, fact, columns }) {
  const { t } = useTranslation()
  const date = fact?.value?.date
  const place = fact?.value?.place
  if (date == null && place == null) {
    return null
  }
  return (
    <>
      <Cell columns={columns}>
        <DmcFactBlock
          heading={`${heading} (${t('original', 'Original')})`}
          date={date?.original}
          place={place?.original}
        />
      </Cell>
      <Cell columns={columns}>
        <DmcFactBlock
          heading={`${heading} (${t('standards.label', 'Standards')})`}
          date={date?.normalized?.[0]?.value}
          place={place?.names?.[0]?.value}
        />
      </Cell>
    </>
  )
}

export function LabelsBlock({ personLabels, columns, heading }) {
  if (isNullOrUndefinedOrEmpty(personLabels)) {
    return null
  }

  return (
    <Cell columns={columns || 12}>
      {heading && <DataBlock heading={heading} data="" />}
      {personLabels?.map((adminLabel) => {
        return (
          <DataBlock
            key={adminLabel.humanReadableText}
            heading={adminLabel.humanReadableText || ''}
            data={adminLabel.reason || ''}
          />
        )
      })}
    </Cell>
  )
}

export function BaseSensitiveMessageSummary({
  messageDetails,
  queueCachesState,
  queueCachesActions,
  labelsBlockProps,
}) {
  const { t } = useTranslation()
  const { setQueuesData } = useContext(QueuesContext)
  const { personId, contributorId, personSummary, personLabels, userSummary } = useMessageSummaryData({
    messageDetails,
    queueCachesState,
    queueCachesActions,
  })

  useEffect(() => {
    setQueuesData((prev) => {
      return { ...prev, [personId]: { name: personSummary?.summary?.name } }
    })
  }, [personId, personSummary, setQueuesData])

  if (isNullOrUndefined(personSummary) || isNullOrUndefined(personLabels) || isNullOrUndefined(userSummary)) {
    return null
  }

  return (
    <Grid>
      <PersonCell personId={personId} personSummary={personSummary} columns={6} />
      <ContributorCell contributorId={contributorId} userSummary={userSummary} columns={6} />
      <TreeIdCells treeId={messageDetails?.treeId} isResearcherTree={messageDetails?.researcherTree} />
      {/* Row */}
      <OriginalAndStandardFactBlock heading={t('birth', 'Birth')} fact={personSummary?.birth} columns={6} />
      {/* Row */}
      <OriginalAndStandardFactBlock heading={t('death', 'Death')} fact={personSummary?.death} columns={6} />
      {/* Christening */}
      <OriginalAndStandardFactBlock
        heading={t('christening', 'Christening')}
        fact={personSummary?.christening}
        columns={6}
      />
      {/* Marriage Information */}
      {personSummary?.marriages?.length > 0 && (
        <Cell columns={6}>
          {personSummary.marriages.map((marriage) => (
            <OriginalAndStandardFactBlock
              key={marriage.value?.conclusionId}
              heading={t('marriage', 'Marriage')}
              fact={marriage}
            />
          ))}
        </Cell>
      )}
      {/* Row */}
      <OriginalAndStandardFactBlock heading={t('burial', 'Burial')} fact={personSummary?.burial} columns={6} />
      {/* Row */}
      <LabelsBlock personLabels={personLabels} {...labelsBlockProps} />
    </Grid>
  )
}
